import { UserRole, UserPlan } from '@types';
import { PLANS, STORAGE_ADDONS as PLAN_STORAGE_ADDONS } from '@config/plans';

export interface SubscriptionTier {
  role: UserRole;
  name: string;
  price: number; // in EUR per month
  storageLimit: number; // in bytes
  maxFiles: number;
  features: string[];
}

export const SUBSCRIPTION_TIERS: Record<UserRole, SubscriptionTier> = {
  [UserRole.FREE]: {
    role: UserRole.FREE,
    name: 'Free',
    price: 0,
    storageLimit: PLANS.free.limits.storage,
    maxFiles: 50,
    features: [
      '1 GB Storage',
      'Up to 50 files',
      'AI Tagging',
      'BPM & Key Detection',
      'Basic file management',
    ],
  },
  [UserRole.STARTER]: {
    role: UserRole.STARTER,
    name: 'Lite',
    price: 2.99,
    storageLimit: PLANS.bedroom.limits.storage,
    maxFiles: 500,
    features: [
      '250 GB Storage',
      'Up to 500 files',
      'AI Tagging',
      'BPM & Key Detection',
      'Advanced Search',
      'Custom Tags',
      'Collaboration',
    ],
  },
  [UserRole.PRO]: {
    role: UserRole.PRO,
    name: 'Pro',
    price: 4.99,
    storageLimit: PLANS.pro.limits.storage,
    maxFiles: 2000,
    features: [
      '500 GB Storage',
      'Up to 2000 files',
      'All Lite features',
      'VST Integration',
      'API Access',
      'Version History',
      'Priority Support',
    ],
  },
  [UserRole.PREMIUM]: {
    role: UserRole.PREMIUM,
    name: 'Studio',
    price: 9.99,
    storageLimit: PLANS.studio.limits.storage,
    maxFiles: 10000,
    features: [
      '5 TB Storage',
      'Up to 10,000 files',
      'All Pro features',
      'Custom AI Models',
      'Stem Separation',
      'Priority VST',
      'API Webhooks',
      'Premium Support',
    ],
  },
};

const PLAN_ROLE_MAP: Record<UserPlan, UserRole> = {
  [UserPlan.FREE]: UserRole.FREE,
  [UserPlan.BEDROOM]: UserRole.STARTER,
  [UserPlan.PRO]: UserRole.PRO,
  [UserPlan.STUDIO]: UserRole.PREMIUM,
};

export const getRoleForPlan = (plan: UserPlan | null | undefined): UserRole => {
  if (!plan) {
    return UserRole.FREE;
  }
  return PLAN_ROLE_MAP[plan] ?? UserRole.FREE;
};

// Storage add-ons (for users who need more storage without upgrading)
export interface StorageAddon {
  id: string;
  name: string;
  size: number; // in bytes
  price: number; // in EUR per month
}

export const STORAGE_ADDONS: StorageAddon[] = PLAN_STORAGE_ADDONS.map((addon) => ({
  id: addon.id,
  name: `+${addon.name} Storage`,
  size: addon.storage,
  price: addon.price.monthly,
}));

// Helper function to format bytes to human-readable
export function formatStorageSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

// Helper function to get percentage of storage used
export function getStoragePercentage(used: number, limit: number): number {
  return Math.min(Math.round((used / limit) * 100), 100);
}

